import { useState, useEffect } from "react";
import * as styles from "./accordion.module.scss"
import { AccordionModuleFieldsFragment } from "../../../../graphql-types"
import React from "react";
import { graphql } from "gatsby";
import Cross from "../../../../static/vectors/thin_cross.svg"
import Arrow from "../../../../static/vectors/diagonal_arrow.svg"
import { ReactSVG } from "react-svg";
import useWindowWidth from "../../../hooks/use-window-width";

type AccordionProps = {
    data: AccordionModuleFieldsFragment
}

const AccordionItem = ({ title, subtitle, isOpen, onClick }) => {
    const [isExiting, setIsExiting] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setIsExiting(true);
            const timer = setTimeout(() => {
                setIsExiting(false);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    if (isOpen) {
        return (
            <button className={styles.openedItem} onClick={onClick}>
                <div className={styles.itemTitleContainer}>
                    <h3 className={styles.openedItemTitle}>{title}</h3>
                    <p className={styles.itemText}>{subtitle}</p>
                </div>
                <div className={styles.cross}>
                    <ReactSVG className={styles.pictogram} src={Cross} />
                </div>
            </button>
        );
    }

    return (
        <button
            className={`${styles.closedItem} ${isExiting ? styles.exiting : ''}`} 
            onClick={onClick}
        >
            <div className={styles.itemTitleContainer}>
                <h3 className={styles.closedItemTitle}>{title}</h3>
            </div>
            <ReactSVG className={styles.pictogram} src={Arrow} />
        </button>
    );
}

const Accordion: React.FC<AccordionProps> = ({ data }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const isMobile = useWindowWidth() <= 825

    const handleAccordionClick = (index) => {
        setActiveIndex(index === activeIndex ? activeIndex : index);
    };

    if (isMobile) {
        return (
            <section className={styles.container}>
                <h2 className={styles.title}>{data.title}</h2>
                <p className={styles.subtitle}>{data.subtitle}</p>
                <div className={styles.accordionContainer}>
                    <div className={styles.accordion}>
                        {data.content.map((item, index) => (
                            <div key={item.title}>
                                <AccordionItem
                                    title={item.title}
                                    subtitle={item.description}
                                    isOpen={index === activeIndex}
                                    onClick={() => handleAccordionClick(index)}
                                />
                                {index === activeIndex && (
                                    <div className={styles.imageTextContainer}>
                                        {item.imageTextBool ? (
                                            <div className={styles.textContainer}>
                                                <p className={styles.imageText}>{item.text}</p>
                                            </div>
                                        ) : (
                                            <img alt={item.image?.alt ?? ""} title={item.image?.title ?? ""} src={item.image?.url ?? ""} className={styles.image} />
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    return (
        <section className={styles.container}>
            <div className={styles.titleContainer}>
                <h2 className={styles.title}>{data.title}</h2>
                {data.subtitle && <p className={styles.subtitle}>{data.subtitle}</p>}
            </div>
            <div className={styles.accordionContainer}>
                <div className={styles.accordion}>
                    {data.content.map((item, index) => (
                        <AccordionItem
                            key={item.title}
                            title={item.title}
                            subtitle={item.description}
                            isOpen={index === activeIndex}
                            onClick={() => handleAccordionClick(index)}
                        />
                    ))}
                </div>
                <div className={styles.imageTextContainer}>
                    {data.content[activeIndex].imageTextBool ? (
                        <div className={styles.textContainer}>
                            <p className={styles.imageText}>{data.content[activeIndex].text}</p>
                        </div>
                    ) : (
                        <img alt={data.content[activeIndex].image?.alt ?? ""} title={data.content[activeIndex].image?.title ?? ""} src={data.content[activeIndex].image?.url ?? ""} className={styles.image} />
                    )}
                </div>
            </div>
        </section>
    )
}

export const fragment = graphql`
  fragment OurApproachAccordionFields on DatoCmsOurApproachAccordion {
    __typename
    id
    title
    subtitle
    content {
        title
        description
        imageTextBool
        image {
            url
            alt
            format
        }
        text
    }
  }
`

export default Accordion;